import React, { useState } from 'react';
import { Card, Typography, Tree, Input, Space, Anchor, Button, Tag, Alert } from 'antd';
import { SearchOutlined, BookOutlined, VideoCameraOutlined, DownloadOutlined, StarOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Link } = Anchor;

interface DocSection {
  key: string;
  title: string;
  content: string;
  children?: DocSection[];
  hasVideo?: boolean;
  hasDownload?: boolean;
  isNew?: boolean;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
}

const documentation: DocSection[] = [
  {
    key: 'getting-started',
    title: 'Getting Started',
    content: `
# Getting Started with PA CRM

Welcome to the Public Adjuster CRM! This comprehensive guide will help you get started with the application and maximize your productivity.

## 🚀 Quick Start Guide

### First Time Setup
1. **Complete Your Profile**: Add your photo, contact information, and preferences
2. **Explore the Dashboard**: Familiarize yourself with key metrics and quick actions
3. **Create Your First Claim**: Start with a simple claim to learn the workflow
4. **Set Up Notifications**: Configure email and in-app notifications
5. **Import Existing Data**: Use the import tools to bring in your current data

### Dashboard Overview
Your dashboard provides a comprehensive view of your business:
- **Active Claims**: Current claims with status indicators
- **Performance Metrics**: Key business metrics and trends
- **Quick Actions**: Common tasks and shortcuts
- **Recent Activity**: Latest updates and communications
- **Calendar**: Upcoming deadlines and appointments

### Navigation Menu
- **Dashboard**: Your home screen with key metrics
- **Claims**: Manage all insurance claims
- **Clients**: Access client profiles and information
- **Tasks**: View and manage your workflow
- **Financial**: Track settlements and payments
- **Reports**: Generate analytics and reports
- **Documents**: Access your document library
- **Settings**: Configure your preferences

## 📱 Mobile App Setup

### Download and Install
1. Download the PA CRM mobile app from your device's app store
2. Log in with your existing credentials
3. Enable biometric authentication for quick access
4. Configure push notifications for important updates

### Mobile Features
- **On-site Assessments**: Capture photos and videos directly
- **Quick Claim Updates**: Update claim status and add notes
- **Offline Mode**: Work without internet connection
- **GPS Integration**: Automatically tag location data
- **Voice Notes**: Record audio notes for hands-free operation

## 🔧 System Requirements

### Browser Requirements
- **Chrome**: Version 90 or later
- **Firefox**: Version 88 or later
- **Safari**: Version 14 or later
- **Edge**: Version 90 or later

### Device Requirements
- **Desktop**: 4GB RAM minimum, 8GB recommended
- **Mobile**: iOS 12+ or Android 8+
- **Storage**: 2GB free space minimum
- **Internet**: 5+ Mbps connection recommended

### Required Settings
- JavaScript enabled
- Cookies enabled
- Pop-up blocker configured to allow the application
- Sufficient storage space for temporary files
    `,
    children: [
      {
        key: 'browser-requirements',
        title: 'Browser Requirements',
        content: `
# Browser Requirements

## Supported Browsers
- **Chrome**: Version 90 or later (recommended)
- **Firefox**: Version 88 or later
- **Safari**: Version 14 or later
- **Edge**: Version 90 or later

## Required Settings
- JavaScript enabled
- Cookies enabled
- Pop-up blocker configured to allow the application
- Sufficient storage space for temporary files

## Mobile Device Requirements

### iOS Requirements
- iOS 12.0 or later
- At least 2GB available storage
- Camera and microphone permissions enabled
- Location services enabled (optional)

### Android Requirements
- Android 8.0 or later
- At least 2GB available storage
- Camera and microphone permissions enabled
- Location services enabled (optional)

## Performance Optimization
- Clear browser cache regularly
- Close unnecessary browser tabs
- Use a wired connection when possible
- Update your browser to the latest version
        `,
      },
      {
        key: 'first-steps',
        title: 'First Steps',
        content: `
# First Steps

## Complete Your Profile Setup
1. **Personal Information**: Add your photo and contact details
2. **Preferences**: Set timezone, date format, and language
3. **Notification Settings**: Configure email and in-app alerts
4. **Email Templates**: Set up professional communication templates
5. **Signature**: Add your digital signature for documents

## Explore Key Features
1. **Dashboard**: Review your business metrics
2. **Claims Management**: Create your first test claim
3. **Client Portal**: Set up client access
4. **Document Library**: Upload sample documents
5. **Reports**: Generate your first report

## Import Existing Data
1. **Client Data**: Import client contact information
2. **Claim History**: Import existing claim data
3. **Documents**: Upload existing documentation
4. **Financial Records**: Import payment and settlement data
        `,
        hasVideo: true,
        difficulty: 'beginner',
      },
    ],
    hasVideo: true,
    difficulty: 'beginner',
  },
  {
    key: 'claims-management',
    title: 'Claims Management',
    content: `
# Claims Management

The Claims Management section is the heart of your Public Adjuster CRM, providing comprehensive tools for managing insurance claims from initial filing through final settlement.

## 📋 Creating a New Claim

### Step-by-Step Process
1. **Click "New Claim"** from the Claims section or dashboard
2. **Enter Basic Information**:
   - Claim number (if available)
   - Client name and contact information
   - Insurance company details
   - Policy number and coverage information
   - Date of loss and type of loss
3. **Add Property Details**:
   - Property address and type
   - Property value and characteristics
   - Additional properties if applicable
4. **Upload Initial Documentation**:
   - Photos of damage
   - Insurance policy documents
   - Initial correspondence
5. **Save and Continue** adding detailed information

### Claim Information Fields
- **Claim Number**: Insurance company claim number
- **Client Information**: Complete contact and property details
- **Insurance Company**: Company name, adjuster, and contact info
- **Policy Details**: Policy number, coverage limits, deductibles
- **Loss Information**: Date, type, and description of loss
- **Property Details**: Address, type, value, and characteristics

## 🔄 Managing Claim Status

### Status Workflow
1. **New**: Initial claim creation
2. **Under Review**: Being evaluated
3. **In Progress**: Active work being done
4. **Pending Settlement**: Waiting for insurance response
5. **Settlement Offered**: Insurance company has made an offer
6. **Settlement Accepted**: Claim resolved
7. **Closed**: Final documentation complete

### Status Updates
- Use the status dropdown to update claim progress
- Add detailed notes when changing status
- Set follow-up reminders for next steps
- Track time spent in each status

## 📸 Damage Assessment

### Photo Documentation
- **Upload Photos**: High-quality images of all damage
- **Video Documentation**: Record walk-through videos
- **Organize by Room**: Categorize photos by area
- **Add Descriptions**: Detailed notes for each photo
- **GPS Tagging**: Automatically tag location data

### Assessment Tools
- **Damage Calculator**: Built-in cost estimation tools
- **Room-by-Room Assessment**: Systematic damage evaluation
- **Repair Estimates**: Detailed cost breakdowns
- **Contractor Quotes**: Track multiple estimates
- **Timeline Creation**: Document damage progression

## 📞 Insurance Communications

### Communication Tracking
- **Log All Interactions**: Phone calls, emails, letters
- **Record Outcomes**: Document responses and decisions
- **Set Follow-ups**: Schedule next contact dates
- **Track Response Times**: Monitor insurance company responsiveness

### Communication Templates
- **Initial Contact**: Professional introduction letters
- **Status Updates**: Regular progress reports
- **Settlement Requests**: Formal demand letters
- **Follow-up Letters**: Persistent communication templates

## 📊 Claim Analytics

### Performance Metrics
- **Claim Success Rate**: Percentage of successful settlements
- **Average Settlement Time**: Time from filing to settlement
- **Settlement Amounts**: Average and total settlement values
- **Client Satisfaction**: Feedback and ratings

### Reporting Tools
- **Claim Reports**: Detailed claim analysis
- **Financial Reports**: Settlement and commission tracking
- **Performance Dashboards**: Real-time metrics
- **Export Capabilities**: PDF, Excel, and CSV formats
    `,
    children: [
      {
        key: 'claim-categories',
        title: 'Claim Categories and Filtering',
        content: `
# Claim Categories and Filtering

## Claim Types
- **Water Damage**: Flood, plumbing, roof leaks
- **Fire Damage**: Structural and content damage
- **Wind/Storm**: Hurricane, tornado, hail damage
- **Theft**: Burglary and vandalism claims
- **Other**: Miscellaneous damage types

## Filtering Options
- **Status Filter**: Filter by current claim status
- **Date Range**: Filter by creation or loss date
- **Client Filter**: Filter by specific client
- **Insurance Company**: Filter by insurance provider
- **Amount Range**: Filter by claim value
- **Priority Level**: Filter by urgency

## Sorting Options
- **Date Created**: Most recent first
- **Status**: Group by claim status
- **Priority**: High priority claims first
- **Client Name**: Alphabetical by client
- **Settlement Amount**: Highest value first
- **Custom**: User-defined sorting

## Advanced Search
- **Full-Text Search**: Search across all claim fields
- **Tag Search**: Search by custom tags
- **Document Search**: Search within uploaded documents
- **Note Search**: Search within claim notes
        `,
      },
      {
        key: 'damage-assessment',
        title: 'Damage Assessment Tools',
        content: `
# Damage Assessment Tools

## Photo Documentation
- **High-Resolution Photos**: Capture detailed damage images
- **Video Documentation**: Record comprehensive walk-throughs
- **Before/After Photos**: Document damage progression
- **360° Views**: Create immersive damage documentation
- **Aerial Photography**: Document roof and exterior damage

## Assessment Tools
- **Damage Calculator**: Built-in estimation tools
- **Room-by-Room Assessment**: Systematic evaluation
- **Material Cost Database**: Current pricing information
- **Labor Cost Estimator**: Professional labor rates
- **Total Loss Calculator**: Determine if vehicle is totaled

## Documentation Features
- **GPS Tagging**: Automatic location tracking
- **Weather Data**: Historical weather information
- **Building Codes**: Local code requirements
- **Contractor Database**: Qualified contractor listings
- **Permit Requirements**: Local permit information
        `,
        hasVideo: true,
        difficulty: 'intermediate',
      },
    ],
    hasVideo: true,
    difficulty: 'beginner',
  },
  {
    key: 'client-management',
    title: 'Client Management',
    content: `
# Client Management

Effective client management is crucial for successful public adjusting. Our CRM provides comprehensive tools for managing client relationships, communications, and information.

## 👥 Creating Client Profiles

### Basic Information
1. **Contact Details**: Name, phone, email, address
2. **Emergency Contacts**: Family members or trusted contacts
3. **Communication Preferences**: Preferred method and frequency
4. **Language Preferences**: Primary and secondary languages
5. **Accessibility Needs**: Any special requirements

### Property Information
- **Primary Residence**: Main property details
- **Additional Properties**: Secondary properties
- **Property Characteristics**: Age, size, construction type
- **Insurance Information**: Current policies and coverage
- **Property Photos**: Visual documentation

### Insurance Details
- **Policy Information**: Company, policy number, coverage
- **Coverage Limits**: Detailed coverage breakdown
- **Deductibles**: Policy deductible amounts
- **Additional Insured**: Other named insured parties
- **Policy Documents**: Uploaded policy copies

## 📞 Communication Tracking

### Interaction Logging
- **Phone Calls**: Log all phone conversations
- **Emails**: Track email communications
- **Meetings**: Record in-person meetings
- **Text Messages**: Track SMS communications
- **Portal Messages**: Client portal interactions

### Communication Features
- **Outcome Tracking**: Document results of each interaction
- **Follow-up Scheduling**: Set reminders for next contact
- **Satisfaction Monitoring**: Track client satisfaction levels
- **Response Time Tracking**: Monitor communication responsiveness

### Automated Communications
- **Status Updates**: Automatic progress notifications
- **Settlement Alerts**: Payment and settlement notifications
- **Document Sharing**: Secure document delivery
- **Appointment Reminders**: Meeting and deadline reminders

## 🏠 Client Portal

### Portal Features
- **Secure Access**: Password-protected client portal
- **Document Sharing**: Secure document delivery
- **Status Updates**: Real-time claim status
- **Communication Center**: Direct messaging system
- **Payment Tracking**: Settlement and payment information

### Portal Benefits
- **24/7 Access**: Clients can check status anytime
- **Reduced Phone Calls**: Self-service reduces inquiries
- **Document Access**: Instant access to important documents
- **Transparency**: Builds trust through openness
- **Efficiency**: Reduces administrative workload

## 📊 Client Analytics

### Performance Metrics
- **Client Satisfaction**: Regular satisfaction surveys
- **Response Times**: How quickly you respond to clients
- **Settlement Success**: Success rate by client type
- **Referral Tracking**: New clients from referrals
- **Retention Rate**: Client retention over time

### Reporting Tools
- **Client Reports**: Detailed client analysis
- **Communication Reports**: Interaction frequency and quality
- **Satisfaction Reports**: Client feedback analysis
- **Referral Reports**: Referral source tracking
    `,
    children: [
      {
        key: 'contact-organization',
        title: 'Contact Organization',
        content: `
# Contact Organization

## Contact Categories
- **Clients**: Current and potential clients
- **Insurance Adjusters**: Insurance company representatives
- **Contractors**: Repair and restoration contractors
- **Attorneys**: Legal professionals
- **Vendors**: Service providers and suppliers
- **Referral Sources**: Professionals who refer clients

## Group Management
- **Custom Groups**: Create groups for specific purposes
- **Tag System**: Use tags for flexible categorization
- **Hierarchical Organization**: Organize by region, type, etc.
- **Search and Filter**: Quick access to specific contacts
- **Import/Export**: Bulk contact management

## Communication Preferences
- **Preferred Method**: Phone, email, text, portal
- **Best Times**: Optimal contact times
- **Language**: Primary and secondary languages
- **Frequency**: How often to contact
- **Special Notes**: Important preferences or restrictions
        `,
      },
    ],
    difficulty: 'beginner',
  },
  {
    key: 'document-management',
    title: 'Document Management',
    content: `
# Document Management

Comprehensive document management is essential for successful claim handling. Our system provides secure, organized, and accessible document storage.

## 📁 Document Organization

### File Categories
- **Claim Documents**: Insurance policies, claim forms
- **Damage Photos**: Before, during, and after photos
- **Estimates**: Contractor and adjuster estimates
- **Contracts**: Service agreements and contracts
- **Correspondence**: Letters, emails, and communications
- **Financial Documents**: Invoices, receipts, payments
- **Legal Documents**: Legal correspondence and filings

### Organization Features
- **Automatic Categorization**: AI-powered document sorting
- **Custom Tags**: User-defined tagging system
- **Search Functionality**: Full-text search across documents
- **Version Control**: Track document versions and changes
- **Access Control**: Role-based document access

## 🔒 Security and Compliance

### Security Features
- **Encryption**: All documents encrypted at rest and in transit
- **Access Logs**: Complete audit trail of document access
- **Backup Systems**: Multiple redundant backup systems
- **Compliance**: HIPAA and insurance industry compliance
- **Data Retention**: Configurable retention policies

### Compliance Requirements
- **HIPAA Compliance**: Protected health information
- **Insurance Regulations**: State-specific requirements
- **Document Retention**: Legal retention requirements
- **Audit Trails**: Complete access and modification logs
- **Secure Sharing**: Encrypted document sharing

## 📤 Upload and Sharing

### Upload Features
- **Drag and Drop**: Easy file upload interface
- **Batch Upload**: Upload multiple files at once
- **Mobile Upload**: Upload directly from mobile devices
- **Auto-OCR**: Automatic text recognition for scanned documents
- **File Validation**: Automatic file type and size validation

### Sharing Capabilities
- **Secure Links**: Password-protected sharing links
- **Expiration Dates**: Automatic link expiration
- **Download Tracking**: Monitor who downloads documents
- **Client Portal**: Secure client document access
- **Email Integration**: Direct email sharing

## 🔍 Search and Retrieval

### Search Features
- **Full-Text Search**: Search within document content
- **Metadata Search**: Search by date, type, tags
- **Advanced Filters**: Multiple filter combinations
- **Saved Searches**: Save frequently used searches
- **Search History**: Track recent searches

### Retrieval Tools
- **Quick Access**: Recently accessed documents
- **Favorites**: Bookmark important documents
- **Related Documents**: AI-suggested related files
- **Document Preview**: Preview without downloading
- **Bulk Operations**: Mass download and organization
    `,
    children: [
      {
        key: 'report-generation',
        title: 'Report Generation',
        content: `
# Report Generation

## Report Types
- **Claim Reports**: Detailed claim analysis and status
- **Financial Reports**: Settlement and payment tracking
- **Performance Reports**: Business metrics and analytics
- **Client Reports**: Client relationship analysis
- **Compliance Reports**: Regulatory and audit reports

## Customization Options
- **Date Ranges**: Flexible time period selection
- **Filters**: Multiple filter combinations
- **Formatting**: Custom headers, footers, and styling
- **Charts and Graphs**: Visual data representation
- **Export Formats**: PDF, Excel, CSV, and Word

## Scheduling and Automation
- **Scheduled Reports**: Automatic report generation
- **Email Delivery**: Automatic email distribution
- **Client Sharing**: Secure client report sharing
- **Archive Management**: Automatic report archiving
- **Version Control**: Track report versions and changes
        `,
        hasVideo: true,
        difficulty: 'intermediate',
      },
    ],
    hasVideo: true,
    difficulty: 'intermediate',
  },
  {
    key: 'financial-management',
    title: 'Financial Management',
    content: `
# Financial Management

Comprehensive financial tracking and management tools to help you maximize your business profitability and maintain accurate records.

## 💰 Settlement Tracking

### Settlement Process
1. **Settlement Offer**: Record initial insurance offer
2. **Negotiation**: Track counter-offers and negotiations
3. **Final Settlement**: Record agreed settlement amount
4. **Payment Processing**: Track payment receipt
5. **Commission Calculation**: Automatic fee calculation

### Settlement Features
- **Multiple Offers**: Track multiple settlement offers
- **Negotiation History**: Complete negotiation timeline
- **Payment Scheduling**: Track payment installments
- **Commission Splits**: Handle team commission sharing
- **Tax Reporting**: Generate tax-ready reports

## 📊 Financial Analytics

### Key Metrics
- **Total Settlements**: Aggregate settlement amounts
- **Average Settlement**: Mean settlement value
- **Success Rate**: Percentage of successful claims
- **Commission Revenue**: Total commission earned
- **Profit Margins**: Business profitability analysis

### Financial Reports
- **Monthly Reports**: Monthly financial summaries
- **Annual Reports**: Year-end financial analysis
- **Client Profitability**: Profitability by client
- **Claim Type Analysis**: Performance by claim type
- **Cash Flow Analysis**: Revenue and expense tracking

## 💳 Payment Processing

### Payment Methods
- **Direct Deposit**: Electronic fund transfers
- **Check Processing**: Physical check handling
- **Credit Card**: Credit card payment processing
- **Wire Transfers**: International wire transfers
- **Payment Plans**: Installment payment tracking

### Payment Features
- **Payment Tracking**: Monitor payment status
- **Late Payment Alerts**: Automatic late payment notifications
- **Payment History**: Complete payment records
- **Reconciliation**: Bank statement reconciliation
- **Tax Reporting**: 1099 and tax form generation

## 🏦 Accounting Integration

### Integration Options
- **QuickBooks**: Direct QuickBooks integration
- **Xero**: Xero accounting software integration
- **Sage**: Sage accounting software integration
- **Custom Export**: CSV export for custom systems
- **API Access**: Direct API integration

### Integration Features
- **Automatic Sync**: Real-time data synchronization
- **Two-Way Sync**: Bidirectional data flow
- **Error Handling**: Automatic error detection and resolution
- **Data Validation**: Automatic data validation
- **Backup Systems**: Redundant data protection
    `,
    children: [
      {
        key: 'commission-calculations',
        title: 'Commission Calculations',
        content: `
# Commission Calculations

## Fee Structures
- **Percentage-Based**: Standard percentage of settlement
- **Tiered Rates**: Different rates for different amounts
- **Flat Fees**: Fixed fee regardless of settlement
- **Hybrid Models**: Combination of percentage and flat fees
- **Custom Formulas**: User-defined calculation methods

## Calculation Features
- **Automatic Calculation**: Real-time commission calculation
- **Multiple Rates**: Different rates for different claim types
- **Split Calculations**: Handle team commission sharing
- **Tax Deductions**: Automatic tax calculation
- **Net vs Gross**: Calculate net after expenses

## Reporting Tools
- **Commission Reports**: Detailed commission analysis
- **Tax Reports**: Tax-ready commission reports
- **Team Reports**: Team member commission tracking
- **Client Reports**: Commission by client analysis
- **Year-End Reports**: Annual commission summaries
        `,
        difficulty: 'intermediate',
      },
    ],
    difficulty: 'intermediate',
  },
  {
    key: 'integrations-tools',
    title: 'Integrations & Tools',
    content: `
# Integrations & Tools

Extend the functionality of your PA CRM with powerful integrations and specialized tools designed for public adjusters.

## 📧 Email Integration

### Email Features
- **Email Sync**: Automatic email synchronization
- **Template Library**: Professional email templates
- **Auto-Responses**: Automatic response management
- **Email Tracking**: Monitor email open and click rates
- **Signature Management**: Professional email signatures

### Email Templates
- **Initial Contact**: Professional introduction letters
- **Status Updates**: Regular progress reports
- **Settlement Requests**: Formal demand letters
- **Follow-up Letters**: Persistent communication templates
- **Thank You Letters**: Client appreciation messages

## 📅 Calendar Integration

### Calendar Features
- **Appointment Scheduling**: Easy appointment booking
- **Deadline Tracking**: Important deadline reminders
- **Meeting Management**: Meeting scheduling and notes
- **Mobile Sync**: Calendar sync with mobile devices
- **Team Calendar**: Shared team calendar

### Calendar Tools
- **Auto-Scheduling**: Automatic appointment scheduling
- **Reminder System**: Multiple reminder options
- **Conflict Detection**: Automatic conflict detection
- **Travel Time**: Built-in travel time calculation
- **Recurring Events**: Set up recurring appointments

## 📱 Mobile App

### Mobile Features
- **On-site Assessments**: Capture photos and videos
- **Quick Updates**: Update claim status quickly
- **Offline Mode**: Work without internet connection
- **GPS Integration**: Automatic location tracking
- **Voice Notes**: Record audio notes

### Mobile Capabilities
- **Photo Capture**: High-quality photo capture
- **Video Recording**: HD video documentation
- **Document Scanning**: Scan documents with camera
- **Signature Capture**: Digital signature collection
- **Push Notifications**: Real-time notifications

## 🔧 Third-Party Integrations

### Insurance Company APIs
- **Claims Status**: Real-time claim status updates
- **Document Upload**: Direct document submission
- **Payment Tracking**: Real-time payment information
- **Communication Logs**: Automatic communication logging
- **Settlement Tracking**: Real-time settlement updates

### Contractor Management
- **Contractor Database**: Qualified contractor listings
- **Quote Management**: Track multiple estimates
- **Work Scheduling**: Schedule repair work
- **Progress Tracking**: Monitor repair progress
- **Payment Processing**: Handle contractor payments

### Legal Software Integration
- **Document Generation**: Legal document creation
- **Case Management**: Legal case tracking
- **Time Tracking**: Billable time tracking
- **Court Filing**: Electronic court filing
- **Legal Research**: Access to legal databases
    `,
    children: [
      {
        key: 'api-integration',
        title: 'API Integration',
        content: `
# API Integration

## Available APIs
- **Claims API**: Create and manage claims
- **Client API**: Manage client information
- **Document API**: Upload and manage documents
- **Financial API**: Track payments and settlements
- **Reporting API**: Generate custom reports

## Authentication
- **API Keys**: Secure API key authentication
- **OAuth 2.0**: Standard OAuth authentication
- **Rate Limiting**: Automatic rate limiting
- **IP Whitelisting**: Restrict access by IP address
- **Audit Logging**: Complete API access logs

## Documentation
- **REST API**: Standard REST API endpoints
- **Webhook Support**: Real-time event notifications
- **SDK Libraries**: Client libraries for popular languages
- **Code Examples**: Sample code for common tasks
- **Interactive Docs**: Swagger/OpenAPI documentation
        `,
        difficulty: 'advanced',
      },
    ],
    hasVideo: true,
    difficulty: 'intermediate',
  },
  {
    key: 'security-privacy',
    title: 'Security & Privacy',
    content: `
# Security & Privacy

Your data security and privacy are our top priorities. We implement enterprise-grade security measures to protect your sensitive information.

## 🔐 Security Measures

### Data Protection
- **Encryption**: AES-256 encryption for all data
- **SSL/TLS**: Secure data transmission
- **Backup Security**: Encrypted backup systems
- **Access Controls**: Role-based access control
- **Audit Logging**: Complete access audit trails

### Compliance Standards
- **HIPAA Compliance**: Protected health information
- **SOC 2 Type II**: Security and availability controls
- **GDPR Compliance**: European data protection
- **State Regulations**: State-specific insurance regulations
- **Industry Standards**: Insurance industry best practices

## 👥 User Access Control

### Authentication
- **Multi-Factor Authentication**: Enhanced login security
- **Single Sign-On**: Enterprise SSO integration
- **Password Policies**: Strong password requirements
- **Session Management**: Automatic session timeouts
- **Login Monitoring**: Suspicious activity detection

### Role-Based Access
- **Admin Roles**: Full system access
- **Manager Roles**: Team management access
- **User Roles**: Standard user access
- **Client Roles**: Limited client portal access
- **Custom Roles**: User-defined access levels

## 📊 Data Management

### Data Retention
- **Configurable Retention**: User-defined retention periods
- **Automatic Archiving**: Automatic data archiving
- **Legal Holds**: Legal requirement compliance
- **Data Export**: Complete data export capabilities
- **Deletion Policies**: Secure data deletion

### Privacy Controls
- **Data Minimization**: Collect only necessary data
- **Consent Management**: User consent tracking
- **Right to Deletion**: User data deletion rights
- **Data Portability**: Easy data export
- **Privacy Notices**: Clear privacy communications

## 🛡️ Incident Response

### Security Monitoring
- **24/7 Monitoring**: Continuous security monitoring
- **Threat Detection**: Advanced threat detection
- **Incident Response**: Rapid incident response
- **Forensic Analysis**: Detailed security analysis
- **Notification System**: Immediate security alerts

### Recovery Procedures
- **Backup Systems**: Multiple redundant backups
- **Disaster Recovery**: Comprehensive recovery plans
- **Business Continuity**: Minimal downtime procedures
- **Data Restoration**: Quick data restoration
- **Communication Plans**: Clear communication procedures
    `,
    children: [
      {
        key: 'compliance',
        title: 'Compliance & Regulations',
        content: `
# Compliance & Regulations

## Insurance Industry Compliance
- **State Regulations**: Compliance with state insurance laws
- **NAIC Guidelines**: National Association of Insurance Commissioners
- **Licensing Requirements**: State licensing compliance
- **Continuing Education**: CE requirement tracking
- **Ethics Standards**: Professional ethics compliance

## Data Protection Regulations
- **HIPAA**: Health information protection
- **GDPR**: European data protection
- **CCPA**: California privacy protection
- **State Laws**: State-specific privacy laws
- **Industry Standards**: Insurance industry standards

## Audit and Reporting
- **Audit Trails**: Complete activity logging
- **Compliance Reports**: Regulatory compliance reports
- **Documentation**: Complete process documentation
- **Training Records**: Staff training documentation
- **Incident Reports**: Security incident documentation
        `,
        difficulty: 'advanced',
      },
    ],
    difficulty: 'intermediate',
  },
  {
    key: 'troubleshooting',
    title: 'Troubleshooting',
    content: `
# Troubleshooting

Common issues and solutions to help you resolve problems quickly and efficiently.

## 🔧 Common Issues and Solutions

### Login Problems
- **Forgotten Password**: Use the "Reset Password" link on the login page
- **Account Locked**: Contact your administrator after multiple failed attempts
- **Two-Factor Issues**: Use backup codes or contact support
- **Browser Issues**: Clear cache and cookies, try different browser
- **Network Problems**: Check internet connection and firewall settings

### Document Upload Issues
- **File Too Large**: Compress images or split large files
- **Unsupported Format**: Convert to supported file types
- **Upload Fails**: Check internet connection and try again
- **Browser Compatibility**: Use supported browser versions
- **Storage Space**: Ensure sufficient storage space

### Performance Issues
- **Slow Loading**: Clear browser cache and cookies
- **Sync Problems**: Refresh the page or restart the application
- **Mobile App Issues**: Update to the latest version
- **Memory Issues**: Close unnecessary browser tabs
- **Network Issues**: Check internet connection speed

### Data Access Problems
- **Missing Information**: Check user permissions and access levels
- **Incorrect Data**: Verify data entry and contact support if needed
- **Sync Errors**: Force a manual sync or contact technical support
- **Import Issues**: Check file format and data structure
- **Export Problems**: Verify export settings and file permissions

## 🚨 Emergency Procedures

### System Outages
1. **Check Status Page**: Visit our status page for updates
2. **Contact Support**: Reach out to our 24/7 support team
3. **Use Mobile App**: Switch to mobile app if web is down
4. **Backup Procedures**: Follow offline backup procedures
5. **Communication Plan**: Notify clients of any delays

### Data Recovery
1. **Check Recently Deleted**: Look in recently deleted items
2. **Contact Support**: Contact support for data recovery
3. **Backup Restoration**: Use backup systems if available
4. **Documentation**: Document the incident for future reference
5. **Prevention**: Implement measures to prevent recurrence

## 📞 Getting Help

### Support Channels
- **In-App Support**: Click the help button in any section
- **Email Support**: support@pacrm.com
- **Phone Support**: 1-800-PA-HELP (1-800-724-3577)
- **Live Chat**: Available during business hours
- **Support Portal**: Access at support.pacrm.com

### Before Contacting Support
1. **Check Documentation**: Review relevant documentation
2. **Clear Cache**: Clear browser cache and cookies
3. **Restart Application**: Close and reopen the application
4. **Check Network**: Verify internet connection
5. **Document Issue**: Note error messages and steps to reproduce

## ⚡ Quick Reference

### Keyboard Shortcuts
- **Ctrl + N**: Create new claim
- **Ctrl + F**: Search/Find
- **Ctrl + S**: Save current changes
- **Alt + D**: Go to Dashboard
- **Alt + C**: Go to Claims
- **Alt + T**: Go to Tasks
- **F1**: Open help system
- **Esc**: Close dialogs and menus

### System Status
- **Green**: All systems operational
- **Yellow**: Minor issues, some features affected
- **Red**: Major issues, contact support
- **Gray**: Maintenance in progress

### Performance Tips
- **Regular Updates**: Keep browser and app updated
- **Clear Cache**: Clear browser cache weekly
- **Close Tabs**: Close unnecessary browser tabs
- **Stable Connection**: Use wired connection when possible
- **Restart Regularly**: Restart application weekly
    `,
    isNew: true,
    difficulty: 'beginner',
  },
  {
    key: 'support',
    title: 'Support',
    content: `
# Support

Get help when you need it with our comprehensive support resources and contact options.

## 🆘 Getting Help

### In-App Support
- **Context-Sensitive Help**: Click the help button in any section
- **Search Function**: Search help system for specific topics
- **Video Tutorials**: Step-by-step video guides
- **Interactive Guides**: Interactive walkthroughs
- **FAQ Database**: Comprehensive frequently asked questions

### Contact Options
- **Email Support**: support@pacrm.com
- **Phone Support**: 1-800-PA-HELP (1-800-724-3577)
- **Live Chat**: Available during business hours (8 AM - 6 PM EST)
- **Support Portal**: Access at support.pacrm.com for ticket tracking
- **Emergency Support**: 24/7 emergency support line

## 📚 Training Resources

### Video Tutorials
- **Getting Started Guide**: Complete system overview
- **Claims Management Workflows**: Step-by-step claim processes
- **Document Management Best Practices**: File organization and security
- **Mobile App Training**: Mobile device usage
- **Advanced Features Overview**: Power user features
- **Reporting and Analytics**: Data analysis and reporting
- **Integration Setup**: Third-party integrations
- **Security Best Practices**: Data protection and privacy

### Webinar Schedule
- **Monthly Training Sessions**: New user orientation
- **Feature Updates**: New feature announcements
- **Best Practices Sharing**: Industry best practices
- **Q&A Sessions**: Interactive question and answer
- **Advanced Training**: Power user workshops
- **Compliance Updates**: Regulatory requirement updates

## 🔄 System Updates and Maintenance

### Regular Updates
- **Feature Enhancements**: Monthly feature releases
- **Security Updates**: Automatic security patches
- **Performance Improvements**: Regular performance optimizations
- **Bug Fixes**: Continuous bug resolution
- **Compliance Updates**: Regulatory requirement updates

### Maintenance Schedule
- **Scheduled Maintenance**: Planned maintenance windows
- **Emergency Maintenance**: Critical issue resolution
- **Backup Procedures**: Regular data backup
- **System Monitoring**: 24/7 system monitoring
- **Performance Optimization**: Continuous optimization

### Staying Informed
- **Update Notifications**: Automatic update notifications
- **Release Notes**: Detailed feature descriptions
- **Beta Testing**: Early access to new features
- **Feedback Collection**: User feedback integration
- **Community Forums**: User community discussions

## 📋 Compliance and Legal

### Data Protection
- **HIPAA Compliance**: Protected health information
- **Regular Security Audits**: Annual security assessments
- **Data Retention Policies**: Legal retention requirements
- **Privacy Policy**: Clear privacy communications
- **Terms of Service**: Legal terms and conditions

### Insurance Regulations
- **State Compliance**: State-specific insurance regulations
- **Audit Trail Maintenance**: Legal requirement compliance
- **Document Retention**: Legal document retention
- **Regulatory Reporting**: Required regulatory reports
- **Licensing Compliance**: Professional licensing requirements

## 🎯 Support Levels

### Basic Support
- **Email Support**: Response within 24 hours
- **Documentation Access**: Full documentation access
- **FAQ Access**: Comprehensive FAQ database
- **Video Tutorials**: Step-by-step video guides
- **Community Forums**: User community support

### Premium Support
- **Priority Phone Support**: Direct phone access
- **Live Chat**: Real-time chat support
- **Dedicated Support**: Assigned support representative
- **Training Sessions**: Personalized training
- **Custom Solutions**: Custom feature development

### Enterprise Support
- **24/7 Support**: Round-the-clock support
- **On-Site Training**: On-site training sessions
- **Custom Integration**: Custom system integration
- **Dedicated Account Manager**: Personal account manager
- **SLA Guarantees**: Service level agreements
    `,
    difficulty: 'beginner',
  },
];

const Documentation: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState('getting-started');
  const [searchText, setSearchText] = useState('');

  const findSection = (sections: DocSection[], key: string): DocSection | undefined => {
    for (const section of sections) {
      if (section.key === key) return section;
      if (section.children) {
        const found = findSection(section.children, key);
        if (found) return found;
      }
    }
    return undefined;
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    // Implement search functionality
  };

  const renderTreeNodes = (data: DocSection[]) => {
    return data.map(item => ({
      title: (
        <div className="flex items-center justify-between w-full">
          <span className="flex items-center">
            {item.isNew && <Tag color="green" size="small">NEW</Tag>}
            {item.hasVideo && <VideoCameraOutlined className="mr-2 text-blue-500" />}
            {item.hasDownload && <DownloadOutlined className="mr-2 text-green-500" />}
            {item.difficulty && (
              <Tag color={
                item.difficulty === 'beginner' ? 'green' : 
                item.difficulty === 'intermediate' ? 'orange' : 'red'
              } size="small" className="mr-2">
                {item.difficulty}
              </Tag>
            )}
            {item.title}
          </span>
        </div>
      ),
      key: item.key,
      children: item.children ? renderTreeNodes(item.children) : undefined,
    }));
  };

  const selectedSection = findSection(documentation, selectedKey);

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <div style={{ width: '350px', borderRight: '1px solid #f0f0f0', padding: '16px' }}>
        <div className="mb-4">
          <Title level={4}>Documentation</Title>
          <Text type="secondary">Complete guides and tutorials</Text>
        </div>
        
        <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }}>
          <Search
            placeholder="Search documentation..."
            prefix={<SearchOutlined />}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            size="large"
          />
        </Space>

        <Alert
          message="New Features Available"
          description="Check out our latest updates including mobile app improvements and enhanced reporting tools."
          type="info"
          showIcon
          className="mb-4"
        />

        <Tree
          showLine
          defaultExpandAll
          selectedKeys={[selectedKey]}
          onSelect={(keys) => {
            if (keys.length > 0) {
              setSelectedKey(keys[0] as string);
            }
          }}
          treeData={renderTreeNodes(documentation)}
        />
      </div>
      
      <div style={{ flex: 1, padding: '24px', overflow: 'auto' }}>
        <Card>
          {selectedSection && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <Title level={3}>{selectedSection.title}</Title>
                  {selectedSection.isNew && <Tag color="green">NEW</Tag>}
                  {selectedSection.difficulty && (
                    <Tag color={
                      selectedSection.difficulty === 'beginner' ? 'green' : 
                      selectedSection.difficulty === 'intermediate' ? 'orange' : 'red'
                    }>
                      {selectedSection.difficulty}
                    </Tag>
                  )}
                </div>
                <div className="flex space-x-2">
                  {selectedSection.hasVideo && (
                    <Button icon={<VideoCameraOutlined />} size="small">
                      Watch Video
                    </Button>
                  )}
                  {selectedSection.hasDownload && (
                    <Button icon={<DownloadOutlined />} size="small">
                      Download Guide
                    </Button>
                  )}
                </div>
              </div>
              <ReactMarkdown>{selectedSection.content}</ReactMarkdown>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Documentation; 